import { useCallback } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import Avatar from '@/components/People/Avatar';
import CoverageBoard from '@/components/Tasks/CoverageBoard';
import CutCard from '@/components/General/CutCard';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useQueryClient } from '@tanstack/react-query';

const DetailSection = ({ title, count, headerRight, children }) => (
    <CutCard style={styles.detailSection} contentStyle={styles.detailSectionContent}>
        <View style={styles.detailHeader}>
            <Text style={styles.detailTitle}>{title}</Text>
            <View style={styles.detailHeaderRight}>
                {headerRight}
                <Text style={styles.detailCount}>{count}</Text>
            </View>
        </View>
        {children}
    </CutCard>
);

export default function TaskTeamOperationsScreen() {
    const { teamID } = useLocalSearchParams();
    const router = useRouter();
    const queryClient = useQueryClient();
    const { width } = useWindowDimensions();
    const desktop = width >= 900;
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: team, isLoading: teamLoading, isFetching: teamFetching } = Query.taskTeams.byId({ id: teamID, options: { enabled: !!teamID } });
    const isAssigner = team?.Assigners?.some(assigner => assigner.id === loggedInUser?.id) || false;
    // Mirrors roles/create.jsx edit gating: admin, role owner (RolePeople
    // canManage → loggedInUser.ManagingRoles), or 'Edit Roles' on grouping
    // (non access-control) roles.
    const canEditLinkedRole = !!team?.Role?.id && (loggedInUser?.isAdmin
        || loggedInUser?.ManagingRoles?.some(r => r.id === team.Role.id)
        || (!team.Role?.isAccessControl && loggedInUser?.can?.('Edit Roles')));
    // Team-scoped coverage, not assigner-scoped: every viewer sees the team's
    // upcoming tasks. VolunteerTeamAssigners inside each instance is real DB
    // data, so TaskCard still gates assigner-only controls correctly.
    const { data: coverage = [], isLoading: coverageLoading, isFetching: coverageFetching } = Query.tasks.upcomingTeamTaskInstances({
        teamID,
        horizonDays: 30,
        options: { enabled: !!teamID },
    });

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.taskTeams.byId(teamID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.tasks.upcomingTeamTaskInstances(teamID) });
        queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
    }, [queryClient, teamID]);

    if (teamLoading || coverageLoading) {
        return <View style={styles.loading}><ActivityIndicator size="large" /></View>;
    }

    if (!team) {
        return (
            <View style={styles.loading}>
                <Text style={styles.missingTitle}>Team not found</Text>
                <SolidButton testID="TeamOperationsBack" text="Back to teams" variant="muted" onPress={() => router.replace('/tasks/teams')} />
            </View>
        );
    }

    return (
        <ScrollView style={styles.root} contentContainerStyle={styles.content} refreshControl={<RefreshControl refreshing={teamFetching || coverageFetching} onRefresh={refresh} />}>
            <CutCard style={styles.hero} contentStyle={styles.heroContent}>
                <View style={styles.teamMark}><Unicon name="people-outline" size={26} colorVariant="tasks" /></View>
                <View style={styles.heroCopy}>
                    <Text style={styles.eyebrow}>{isAssigner ? 'MY TEAM · OPERATIONS' : 'TEAM OVERVIEW'}</Text>
                    <Text testID="TeamOperationsName" style={styles.heading}>{team.name}</Text>
                    {!!team.description && <Text style={styles.description}>{team.description}</Text>}
                    {!!team.Location?.name && <View style={styles.location}><Unicon name="location-outline" size={14} colorVariant="textSecondary" /><Text style={styles.locationText}>{team.Location.name}</Text></View>}
                </View>
                {loggedInUser?.can?.('Update Tasks') && <SolidButton testID="TeamOperationsEdit" text="Edit team" iconName="create-outline" iconBefore variant="muted" onPress={() => router.push({ pathname: '/tasks/teams/create', params: { id: team.id } })} />}
            </CutCard>

            <View style={styles.layout(desktop)}>
                <View style={styles.coverageColumn(desktop)}>
                    <View style={styles.coverageHeader}>
                        <View>
                            <Text style={styles.sectionEyebrow}>NEXT 30 DAYS</Text>
                            <Text style={styles.sectionTitle}>Coverage board</Text>
                        </View>
                        {isAssigner && <View style={styles.managerBadge}><Unicon name="key-outline" size={13} colorVariant="tasks" /><Text style={styles.managerBadgeText}>Staffing access</Text></View>}
                    </View>
                    <CoverageBoard taskInstances={coverage} emptyMessage="No upcoming task instances for this team." fromScreen={`/tasks/teams/${teamID}`} />
                </View>

                <View style={styles.detailsColumn(desktop)}>
                    <DetailSection title="Assigners" count={team.Assigners?.length || 0}>
                        {team.Assigners?.length > 0 ? team.Assigners.map(assigner => (
                            <View key={assigner.id} style={styles.personRow}>
                                <Avatar size={34} profile={assigner} />
                                <Text style={styles.rowTitle}>{assigner.firstName} {assigner.lastName}</Text>
                            </View>
                        )) : <Text style={styles.emptyText}>No explicit assigners.</Text>}
                    </DetailSection>

                    <DetailSection title="Members" count={team.Members?.length || 0} headerRight={team.Role?.id && (
                        <>
                            <TouchableOpacity testID="TeamRoleLink" accessibilityRole="button" accessibilityLabel={`Open ${team.Role.name} role`} style={styles.roleLink} onPress={() => router.push(`/roles/${team.Role.id}`)}>
                                <Unicon name="people-circle-outline" size={14} colorVariant="tasks" />
                                <Text style={styles.roleLinkText} numberOfLines={1}>{team.Role.name}</Text>
                            </TouchableOpacity>
                            {canEditLinkedRole && (
                                <TouchableOpacity testID="TeamRoleEdit" accessibilityRole="button" accessibilityLabel={`Edit ${team.Role.name} role`} hitSlop={6} style={styles.roleEditButton} onPress={() => router.push({ pathname: '/roles/create', params: { id: team.Role.id } })}>
                                    <Unicon name="create-outline" size={15} colorVariant="textSecondary" />
                                </TouchableOpacity>
                            )}
                        </>
                    )}>
                        {!team.Role ? <Text style={styles.emptyText}>No member role linked.</Text>
                            : team.Members?.length > 0 ? team.Members.map(member => (
                            <View key={member.id} style={styles.personRow}>
                                <Avatar size={34} profile={member} />
                                <Text style={styles.rowTitle}>{member.firstName} {member.lastName}</Text>
                            </View>
                        )) : <Text style={styles.emptyText}>No members in the {team.Role.name} role.</Text>}
                    </DetailSection>

                    <DetailSection title="Task definitions" count={team.Tasks?.length || 0}>
                        {team.Tasks?.length > 0 ? team.Tasks.map(task => (
                            <TouchableOpacity key={task.id} testID={`TeamOperationsTask-${task.id}`} accessibilityRole="button" accessibilityLabel={`Open ${task.title}`} style={styles.linkRow} onPress={() => router.push(`/tasks/${task.id}`)}>
                                <View style={styles.rowIcon}><Unicon name="clipboard-outline" size={16} colorVariant="tasks" /></View>
                                <View style={styles.rowCopy}><Text style={styles.rowTitle}>{task.title}</Text><Text style={styles.rowMeta}>{task.assigneesNeeded || 1} needed</Text></View>
                                <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                            </TouchableOpacity>
                        )) : <Text style={styles.emptyText}>No task definitions linked.</Text>}
                    </DetailSection>

                    <DetailSection title="Events" count={team.Events?.length || 0}>
                        {team.Events?.length > 0 ? team.Events.map(event => (
                            <TouchableOpacity key={event.id} testID={`TeamOperationsEvent-${event.id}`} accessibilityRole="button" accessibilityLabel={`Open ${event.name}`} style={styles.linkRow} onPress={() => router.push(`/events/${event.id}`)}>
                                <View style={styles.eventDot(event.Calendar?.color)} />
                                <View style={styles.rowCopy}><Text style={styles.rowTitle}>{event.name}</Text><Text style={styles.rowMeta}>{event.Calendar?.name || 'Event'}</Text></View>
                                <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                            </TouchableOpacity>
                        )) : <Text style={styles.emptyText}>No linked events.</Text>}
                    </DetailSection>
                </View>
            </View>
        </ScrollView>
    );
}

const styles = StyleSheet.create(theme => ({
    root: { flex: 1, backgroundColor: 'transparent' },
    loading: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 14, backgroundColor: 'transparent' },
    content: { width: '100%', maxWidth: 1180, alignSelf: 'center', padding: 16, paddingBottom: 100, gap: 22 },
    missingTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 20 },
    hero: { boxShadow: theme.shadows.card },
    heroContent: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 14, padding: 18 },
    teamMark: { width: 54, height: 54, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.tasksContainer },
    heroCopy: { flex: 1, minWidth: 230 },
    eyebrow: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 10, letterSpacing: 1.1 },
    heading: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 25, marginTop: 2 },
    description: { color: theme.colors.textSecondary, fontSize: 13, lineHeight: 19, marginTop: 4, maxWidth: 680 },
    location: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 7 },
    locationText: { color: theme.colors.textSecondary, fontSize: 12 },
    layout: desktop => ({ flexDirection: desktop ? 'row' : 'column', alignItems: 'flex-start', gap: 24 }),
    coverageColumn: desktop => ({ width: desktop ? undefined : '100%', flex: desktop ? 1.35 : undefined, minWidth: 0 }),
    detailsColumn: desktop => ({ width: desktop ? undefined : '100%', flex: desktop ? 0.75 : undefined, minWidth: 0, gap: 14 }),
    coverageHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, marginBottom: 12 },
    sectionEyebrow: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 10, letterSpacing: 1.1 },
    sectionTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 20, marginTop: 2 },
    managerBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: theme.colors.tasksContainer, paddingHorizontal: 9, paddingVertical: 5, borderRadius: 8 },
    managerBadgeText: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 11 },
    detailSection: { boxShadow: theme.shadows.card },
    detailSectionContent: { padding: 14 },
    detailHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 },
    detailHeaderRight: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1 },
    roleLink: { flexDirection: 'row', alignItems: 'center', gap: 5, flexShrink: 1 },
    roleLinkText: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 12, flexShrink: 1 },
    roleEditButton: { padding: 4 },
    detailTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 15 },
    detailCount: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 12, backgroundColor: theme.colors.tasksContainer, minWidth: 24, textAlign: 'center', paddingHorizontal: 6, paddingVertical: 3, borderRadius: 7 },
    personRow: { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 5 },
    linkRow: { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 8 },
    rowIcon: { width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.tasksContainer },
    rowCopy: { flex: 1 },
    rowTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 13 },
    rowMeta: { color: theme.colors.textSecondary, fontSize: 11, marginTop: 1 },
    eventDot: color => ({ width: 10, height: 10, borderRadius: 5, backgroundColor: color || theme.colors.events, marginHorizontal: 11 }),
    emptyText: { color: theme.colors.textSecondary, fontSize: 12, paddingVertical: 6 },
}));
